import {
  isFurnishLesson,
  modelCatalogueOf,
  servedWorldsOf,
  validateLesson,
  type Lesson,
  type ModelCatalogue,
  type ServedWorld,
  withCurrentWorldNames,
} from '@vrlearning/lesson-schema';
import { catalogueOfServed } from './api/served-catalogue.js';

/**
 * A demo build (`pnpm demo:build`, apps/headset README): one lesson built into
 * the player, to put on any web server and show somebody -- no API, no
 * pairing, no session, nothing recorded. `bootDemo` in main.ts plays it the
 * way `?play=` plays a lesson from a ticket, with the lesson taken from the
 * build (`virtual:demo-lesson`) instead of fetched.
 *
 * This is the part that can be tested without a browser.
 */

/** What the viewer reads: an adult being shown it, so "u", as in `?play=`. */
export const DEMO_TEXT = {
  failed: { title: 'Deze demo kan niet starten' },
  ended: {
    title: 'Einde van de demo',
    body: 'Dit was een voorbeeld van een les in VR. Er is niets opgeslagen.',
  },
  again: 'Opnieuw bekijken',
} as const;

export type DemoOutcome = { kind: 'ready'; lesson: Lesson } | { kind: 'failed'; detail: string };

/**
 * The build's lesson, validated before it can reach the renderer -- the same
 * check every other lesson passes, its world judged by the package the build
 * carries (`worlds`, as a served lesson's are). A build made without one gives `null`.
 */
export function demoLesson(
  raw: unknown,
  worlds: Readonly<Record<string, ServedWorld>> = {},
): DemoOutcome {
  if (raw === null || raw === undefined)
    return { kind: 'failed', detail: 'geen les in deze build' };
  const catalogue = catalogueOfServed(worlds);
  const result = validateLesson(raw, { worlds: catalogue });
  // Furnishing needs a class's meubelset and a server to hold it (ADR-037): not in a demo.
  if (result.valid && isFurnishLesson(result.lesson))
    return { kind: 'failed', detail: 'Inrichten kan nog niet in een demo' };
  // A world's old names played as its current ones (spec 2026-10-16).
  if (result.valid)
    return { kind: 'ready', lesson: withCurrentWorldNames(result.lesson, catalogue) };
  const first = result.errors[0];
  return {
    kind: 'failed',
    detail: `les is ongeldig: ${first?.path ?? ''} ${first?.message ?? ''}`.trim(),
  };
}

/** The card for a demo that cannot start: its lesson did not validate, or would not render. */
export function demoProblemText(detail: string): { title: string; body: string } {
  return {
    title: DEMO_TEXT.failed.title,
    body: `De les kon niet worden geladen (${detail}).`,
  };
}

/**
 * The library models a demo build carries (`virtual:demo-models`, ADR-043): checked as a served
 * lesson's `models` are, so a fact that would not render is left out. `{}` in every other build.
 */
export function demoModels(raw: unknown): ModelCatalogue {
  return modelCatalogueOf(raw);
}

/**
 * The world packages a demo build carries (`virtual:demo-worlds`, `pnpm demo:build --werelden`):
 * slug -> the package as the API serves one, its files beside the page under `worlds/`. Kept as a
 * served lesson's `worlds` are; `{}` in every other build.
 */
export function demoWorlds(raw: unknown): Record<string, ServedWorld> {
  return servedWorldsOf(raw);
}
